import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Database, Table, Trash, CaretLeft, CaretRight, Eye } from '@phosphor-icons/react';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export default function AdminDB() {
  const { user } = useAuth();
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const limit = 20;

  useEffect(() => { fetchCollections(); }, []);

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/db/collections`, { withCredentials: true });
      setCollections(res.data.collections);
    } catch (err) {
      console.error('Not authorized or fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCollection = async (name, newSkip = 0) => {
    setActiveCollection(name);
    setSkip(newSkip);
    setExpandedDoc(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/db/${name}?limit=${limit}&skip=${newSkip}`, { withCredentials: true });
      setDocuments(res.data.documents);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Browse error:', err);
    }
  };

  const handleDelete = async (collection, doc) => {
    const idVal = doc.task_id || doc.user_id || doc.insight_id;
    if (!idVal || !window.confirm(`Delete document ${idVal}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/db/${collection}/${idVal}`, { withCredentials: true });
      openCollection(collection, skip);
      fetchCollections();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="p-10 text-center">
          <Database size={48} className="mx-auto mb-4 text-[#acb3b8]" />
          <p className="title-md">Admin access required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 lg:p-10">
        <div className="flex items-center gap-3 mb-8">
          <Database size={28} weight="duotone" className="text-[#005bc1]" />
          <div>
            <h1 className="display-lg text-3xl">Database Explorer</h1>
            <p className="body-lg text-sm mt-1">Browse MongoDB collections and documents</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Collections sidebar */}
          <div className="space-y-2">
            <p className="label-md text-xs mb-3">COLLECTIONS</p>
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[1,2,3].map(i => <div key={i} className="h-12 bg-[#f2f4f6] rounded-xl" />)}
              </div>
            ) : collections.map(col => (
              <button
                key={col.name}
                onClick={() => openCollection(col.name)}
                className={`w-full flex items-center justify-between p-3 rounded-xl text-left transition-all ${
                  activeCollection === col.name
                    ? 'bg-[#005bc1] text-white'
                    : 'bg-[#f2f4f6] hover:bg-[#ebeef2] text-[#2d3338]'
                }`}
                data-testid={`collection-${col.name}`}
              >
                <div className="flex items-center gap-2">
                  <Table size={16} weight="duotone" />
                  <span className="font-medium text-sm">{col.name}</span>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  activeCollection === col.name ? 'bg-white/20' : 'bg-[#d6e3ff] text-[#005bc1]'
                }`}>
                  {col.count}
                </span>
              </button>
            ))}
          </div>

          {/* Documents */}
          <div className="lg:col-span-3">
            {!activeCollection ? (
              <div className="surface-card p-12 text-center">
                <Table size={48} className="mx-auto mb-4 text-[#acb3b8]" />
                <p className="title-md">Select a collection</p>
                <p className="body-lg text-sm mt-2">Click a collection from the left to browse its documents</p>
              </div>
            ) : (
              <div className="surface-card overflow-hidden">
                <div className="flex items-center justify-between p-4 bg-[#f2f4f6]">
                  <div>
                    <span className="font-bold text-[#2d3338]">{activeCollection}</span>
                    <span className="text-sm text-[#596065] ml-2">({total} documents)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openCollection(activeCollection, Math.max(0, skip - limit))}
                      disabled={skip === 0}
                      className="p-1.5 rounded-lg hover:bg-[#ebeef2] disabled:opacity-30"
                    >
                      <CaretLeft size={16} />
                    </button>
                    <span className="text-xs text-[#596065]">{skip + 1}-{Math.min(skip + limit, total)} of {total}</span>
                    <button
                      onClick={() => openCollection(activeCollection, skip + limit)}
                      disabled={skip + limit >= total}
                      className="p-1.5 rounded-lg hover:bg-[#ebeef2] disabled:opacity-30"
                    >
                      <CaretRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-[#f2f4f6]">
                  {documents.map((doc, i) => {
                    const docId = doc.task_id || doc.user_id || doc.insight_id || `doc-${i}`;
                    const isExpanded = expandedDoc === docId;
                    const preview = Object.entries(doc).slice(0, 4).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(' | ');

                    return (
                      <div key={docId} className="group">
                        <div className="flex items-center gap-3 p-4 hover:bg-[#f9f9fb]">
                          <button
                            onClick={() => setExpandedDoc(isExpanded ? null : docId)}
                            className="flex-1 text-left min-w-0"
                          >
                            <p className="text-xs font-mono text-[#005bc1] font-semibold">{docId}</p>
                            <p className="text-xs text-[#596065] truncate mt-1">{preview}</p>
                          </button>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setExpandedDoc(isExpanded ? null : docId)}
                              className="p-1.5 hover:bg-[#d6e3ff] rounded-lg"
                              title="View"
                            >
                              <Eye size={16} className="text-[#005bc1]" />
                            </button>
                            <button
                              onClick={() => handleDelete(activeCollection, doc)}
                              className="p-1.5 hover:bg-[#ff8b9a] rounded-lg"
                              title="Delete"
                            >
                              <Trash size={16} className="text-[#ba1a1a]" />
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="px-4 pb-4">
                            <pre className="bg-[#1e1e2e] text-[#cdd6f4] p-4 rounded-xl text-xs overflow-x-auto max-h-96">
                              {JSON.stringify(doc, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
